import { apiClient } from './apiClient';
import type {
  LoginSectionConfig,
  LoginTemplateId,
  LoginPanelPosition,
  LoginPanelBackgroundConfig,
} from '../types/loginTemplate';

// Additional types for API responses (not in loginTemplate.ts)
export interface LoginPageFeatureCard {
  icon: string;
  title: string;
  description: string;
}

export interface LoginPageStat {
  value: string;
  label: string;
}

export type FormPlacement = 'left' | 'right' | 'center' | 'overlay';

// Layout configuration
export interface LoginPageLayoutConfig {
  showLeftPanel?: boolean;
  showRightPanel?: boolean;
}

// Panel styles configuration
export interface LoginPagePanelStyle {
  backgroundColor?: string;
  textColor?: string;
  background?: LoginPanelBackgroundConfig;
}

// Button styles configuration
export interface LoginPageButtonStyle {
  backgroundColor?: string;
  textColor?: string;
}

// Login form glassmorphism configuration
export interface LoginFormGlassConfig {
  /** 'solid' | 'glass' */
  mode?: string;
  /** 0-100 opacity percentage */
  opacity?: number;
  /** Blur intensity in pixels (0-24) */
  blurPx?: number;
  /** Whether to show the border */
  borderVisible?: boolean;
  /** Surface tint/lightness adjustment (0-100) */
  tint?: number;
}

// Extended styles configuration
export interface LoginPageStylesConfig {
  leftPanel?: LoginPagePanelStyle;
  rightPanel?: LoginPagePanelStyle;
  header?: LoginPagePanelStyle;
  cards?: LoginPagePanelStyle;
  button?: LoginPageButtonStyle;
  loginFormGlass?: LoginFormGlassConfig;
}

// Response Types
export interface LoginPageConfigDto {
  id?: string;
  programId?: string;
  templateId: LoginTemplateId;
  formPlacement: FormPlacement;
  sections: LoginSectionConfig[];
  layout?: LoginPageLayoutConfig;
  logoPosition?: LoginPanelPosition;
  backgroundType?: string;
  backgroundValue?: string;
  backgroundPatternEnabled?: boolean;
  backgroundPatternType?: 'dots' | 'grid' | 'lines' | 'waves' | 'none';
  backgroundPatternOpacity?: number;
  backgroundGradientColor1?: string;
  backgroundGradientColor2?: string;
  gradientDirection?: string;
  textColorHeading?: string;
  textColorSubheading?: string;
  textColorDescription?: string;
  textColorFeatureTitle?: string;
  textColorFeatureDescription?: string;
  textColorStatsLabel?: string;
  textColorStatsValue?: string;
  boxColorFeatureCard?: string;
  boxColorStatsContainer?: string;
  boxColorLoginForm?: string;
  styles?: LoginPageStylesConfig;
  customLogoUrl?: string;
  customTitle?: string;
  customSubtitle?: string;
  customDescription?: string;
  featureCards?: LoginPageFeatureCard[];
  stats?: LoginPageStat[];
  isActive?: boolean;
  createdAt?: string;
  modifiedAt?: string;
}

export interface ResolvedLoginPageConfigDto {
  config: LoginPageConfigDto;
  configSource: 'application' | 'program' | 'default';
  programId?: string;
  programName?: string;
}

export interface SaveLoginPageConfigDto {
  templateId: LoginTemplateId;
  formPlacement: FormPlacement;
  sections: LoginSectionConfig[];
  layout?: LoginPageLayoutConfig;
  logoPosition?: LoginPanelPosition;
  backgroundType?: string;
  backgroundValue?: string;
  backgroundPatternEnabled?: boolean;
  backgroundPatternType?: 'dots' | 'grid' | 'lines' | 'waves' | 'none';
  backgroundPatternOpacity?: number;
  backgroundGradientColor1?: string;
  backgroundGradientColor2?: string;
  gradientDirection?: string;
  textColorHeading?: string;
  textColorSubheading?: string;
  textColorDescription?: string;
  textColorFeatureTitle?: string;
  textColorFeatureDescription?: string;
  textColorStatsLabel?: string;
  textColorStatsValue?: string;
  boxColorFeatureCard?: string;
  boxColorStatsContainer?: string;
  boxColorLoginForm?: string;
  styles?: LoginPageStylesConfig;
  customLogoUrl?: string;
  customTitle?: string;
  customSubtitle?: string;
  customDescription?: string;
  featureCards?: LoginPageFeatureCard[];
  stats?: LoginPageStat[];
}

export interface ProgramLoginPageConfigResponse {
  config: LoginPageConfigDto | null;
  hasCustomConfig: boolean;
  usingApplicationDefault: boolean;
}

/**
 * Login Page Configuration Service
 * Handles fetching and saving login page config at application and program levels
 */
export const loginPageConfigService = {
  /**
   * Get resolved login page configuration for a program (or application default)
   * PUBLIC ENDPOINT - No auth required
   */
  async getResolvedConfig(programCode?: string): Promise<ResolvedLoginPageConfigDto> {
    const url = programCode
      ? `/shared/login-config?programCode=${encodeURIComponent(programCode)}`
      : `/shared/login-config`;

    const response = await apiClient.get(url);
    return response.data.data;
  },

  /**
   * Get application-level login page configuration (SuperAdmin only)
   */
  async getApplicationConfig(): Promise<LoginPageConfigDto | null> {
    const response = await apiClient.get(`/superadmin/login-page-config`);
    return response.data.data;
  },

  /**
   * Save application-level login page configuration (SuperAdmin only)
   */
  async saveApplicationConfig(config: SaveLoginPageConfigDto): Promise<LoginPageConfigDto> {
    const response = await apiClient.post(`/superadmin/login-page-config`, config);
    return response.data.data;
  },

  /**
   * Get program-level login page configuration
   */
  async getProgramConfig(programId: string): Promise<ProgramLoginPageConfigResponse> {
    const response = await apiClient.get(`/programs/${programId}/login-page-config`);
    return response.data.data;
  },

  /**
   * Save program-level login page configuration (creates override)
   */
  async saveProgramConfig(programId: string, config: SaveLoginPageConfigDto): Promise<LoginPageConfigDto> {
    const response = await apiClient.post(
      `/programs/${programId}/login-page-config`,
      config
    );
    return response.data.data;
  },

  /**
   * Delete program-level configuration (revert to application default)
   */
  async deleteProgramConfig(programId: string): Promise<boolean> {
    const response = await apiClient.delete(`/programs/${programId}/login-page-config`);
    return response.data.data.deleted;
  },

  /**
   * Check if program has custom configuration
   */
  async programHasCustomConfig(programId: string): Promise<boolean> {
    const response = await apiClient.get(
      `/programs/${programId}/login-page-config/exists`
    );
    return response.data.data.exists;
  },
};
